import React, { useEffect, useRef } from 'react';
import './AnimatedBackground.css';

const MOUSE_PARTICLE_COUNT = 3;
const FOLLOW_SPEED = 0.15;

interface MouseParticle {
  element: HTMLDivElement;
  x: number;
  y: number;
  tx: number;
  ty: number;
  speed: number;
}

export const AnimatedBackground: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const particlesRef = useRef<MouseParticle[]>([]);
  const rafRef = useRef<number>();
  const isMovingRef = useRef(false);
  const mouseTimerRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // 创建鼠标跟随粒子
    for (let i = 0; i < MOUSE_PARTICLE_COUNT; i++) {
      const element = document.createElement('div');
      element.className = 'mouse-particle';
      container.appendChild(element);

      particlesRef.current.push({
        element,
        x: 0,
        y: 0,
        tx: 0,
        ty: 0,
        speed: FOLLOW_SPEED * (1 - i * 0.2)
      });
    }

    const handleMouseMove = (e: MouseEvent) => {
      const rect = container.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;

      particlesRef.current.forEach(particle => {
        particle.tx = mouseX;
        particle.ty = mouseY;
      });

      isMovingRef.current = true;
      particlesRef.current.forEach(particle => {
        particle.element.classList.add('active');
      });

      if (mouseTimerRef.current) {
        clearTimeout(mouseTimerRef.current);
      }

      mouseTimerRef.current = setTimeout(() => {
        isMovingRef.current = false;
        particlesRef.current.forEach(particle => {
          particle.element.classList.remove('active');
        });
      }, 100);
    };

    const animate = () => {
      if (isMovingRef.current) {
        particlesRef.current.forEach((particle) => {
          // 平滑跟随
          particle.x += (particle.tx - particle.x) * particle.speed;
          particle.y += (particle.ty - particle.y) * particle.speed;

          // 应用位置
          particle.element.style.transform = `translate(${particle.x}px, ${particle.y}px)`;
        });
      }
      rafRef.current = requestAnimationFrame(animate);
    };

    container.addEventListener('mousemove', handleMouseMove);
    animate();

    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
      if (mouseTimerRef.current) {
        clearTimeout(mouseTimerRef.current);
      }
      container.removeEventListener('mousemove', handleMouseMove);
      particlesRef.current.forEach(particle => {
        particle.element.remove();
      });
      particlesRef.current = [];
    };
  }, []);

  return <div ref={containerRef} className="animated-background" />;
};
